import { isInteger } from "lodash";
import moment from "moment";
import CustomizedProgressBars from "../components/ProgressBar";
import {
  CREDENTIAL_SESSION,
  LOGIN_ROUTE,
  logoutUrls,
  SAML_AUTHN_STARTED,
} from "../helper/constants";
import { LoaderActions } from "../redux/slices/componentSlice/loader";
import { NotificationActions } from "../redux/slices/componentSlice/notificationSlice";
import store from "../redux/store";

export const isDevEnv = () => {
  if (window) {
    return window?.location?.origin.includes("localhost");
  }
  return false;
};

/**
 * True when this tab authenticated through a credential form rather than SAML.
 *
 * The post-login reload drops the query param, so logout and retry paths cannot
 * re-read it — they ask "was this a credential session?" instead, and send users
 * back to INTERNAL_LOGIN_ROUTE rather than to OAM. Read this *before* calling
 * clearAuthState(), which wipes the marker along with everything else.
 */
export const isCredentialSession = () => {
  if (typeof sessionStorage === "undefined") return false;
  return sessionStorage.getItem(CREDENTIAL_SESSION) === "true";
};

/**
 * Navigate to SAML login at most once per browser tab while auth is in flight.
 * Multiple redirects were creating several AuthnRequests; OAM then posted back
 * more than one SAML Response and the second failure sent users to /logout-success.
 */
export function redirectToSamlLogin(source = "unknown") {
  if (typeof window === "undefined") return false;

  if (sessionStorage.getItem(SAML_AUTHN_STARTED) === "true") {
    console.warn("[auth] Skipping duplicate SAML login redirect", { source });
    return false;
  }

  sessionStorage.setItem(SAML_AUTHN_STARTED, "true");
  sessionStorage.setItem("sso_initiated", "true");
  console.info("[auth] SAML login redirect", { source, target: LOGIN_ROUTE });
  window.location.href = LOGIN_ROUTE;
  return true;
}

export function clearSamlLoginInProgress() {
  if (typeof sessionStorage !== "undefined") {
    sessionStorage.removeItem(SAML_AUTHN_STARTED);
  }
}

export function clearAuthState() {
  if (typeof window === "undefined") return;

  localStorage.clear();
  sessionStorage.clear();

  document.cookie.split(";").forEach((cookie) => {
    const [name] = cookie.split("=");
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
  });
}

export function setCookie(name, val, days = 30, path = "/") {
  var expires = "";
  var value = val;
  if (typeof value !== "string" && value) {
    value = JSON.stringify(value);
  }
  if (days) {
    var date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = "; expires=" + date.toUTCString();
  }
  // eslint-disable-next-line no-useless-concat
  document.cookie = name + "=" + (value || "") + expires + ";" + `path=${path}`;
}

export function getCookie(name) {
  var nameEQ = name + "=";
  var ca = document.cookie.split(";");
  for (var i = 0; i < ca.length; i++) {
    var c = ca[i];
    while (c.charAt(0) === " ") c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
}

const safePercent = (numerator, denominator, precision = 2) => {
  if (!denominator || denominator === 0) return "0.00";
  return ((numerator / denominator) * 100).toFixed(precision);
};

const safeRatio = (numerator, denominator, precision = 1) => {
  if (!denominator || denominator === 0) return "₹0.0L";
  return "₹" + (numerator / 100000).toFixed(precision) + "L";
};

export function eraseCookie(name) {
  document.cookie = name + "=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;";
  sessionStorage.removeItem("user_details");
}

export function deleteAllCookies(func) {
  var cookies = document.cookie.split(";");

  for (var i = 0; i < cookies.length; i++) {
    var cookie = cookies[i];
    var eqPos = cookie.indexOf("=");
    var name = eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie;
    eraseCookie(name);
  }
}

export const downLoadCsvData = ({ data, fileName, type = ".csv" }) => {
  const blob = new Blob([data], { type: "text/csv" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("hidden", "");
  link.setAttribute("href", url);
  link.setAttribute("download", `${fileName}${type}`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const NOTIFICATION_TYPE = {
  ERROR: "error",
  SUCCESS: "success",
};

export const dispatchNotification = (state) => {
  const {
    type = NOTIFICATION_TYPE.ERROR,
    timeout = 4000,
    message = "",
  } = state;
  store.dispatch(
    NotificationActions.addNotification({
      type,
      timeout,
      message,
    })
  );
};

// Screens that render their own skeletons (e.g. the OneApp home screen) turn the
// global backdrop loader off so one slow API doesn't block the whole page.
let globalLoaderSuppressed = false;

export const showLoader = () => {
  if (globalLoaderSuppressed) return;
  store.dispatch(LoaderActions.showLoader({ show: true }));
};

export const hideLoader = () => {
  store.dispatch(LoaderActions.hideLoader());
};

export const setGlobalLoaderSuppressed = (suppressed) => {
  globalLoaderSuppressed = suppressed;
};

export const isLastIndex = (data = [], index) => {
  return index !== data.length - 1;
};

export const getData = (data) => {
  let formattedData = data;
  if (data) {
    if (!isInteger(data) && typeof data === "string") {
      if (data.includes("%") && isInteger(parseInt(data.split("%")[0]))) {
        formattedData = (
          <span
            style={{
              fontWeight: 700,
              color: data.split("%")[0] > 0 ? "#14923eff" : "#DE3B40FF",
            }}
          >
            {data}
          </span>
        );
      } else if (data.includes("/")) {
        formattedData = (
          <>
            <span style={{ color: "#171A1FFF", fontWeight: 700 }}>
              {data.split("/")[0]}
            </span>
            <span style={{ color: "#DE3B4080", fontWeight: 400 }}>
              /{data.split("/")[1]}
            </span>
          </>
        );
      }
    } else {
      formattedData = data.toLocaleString("en-IN");
    }
  } else {
    formattedData = "-";
  }
  return formattedData;
};

export function dateFormatter(date, format) {
  return moment(date).format(format);
}
export function downloadCSV(data, filename = "data.csv") {
  const blob = new Blob([data], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function buildUrl(baseUrl, params) {
  const queryParams = [];
  const processValue = (key, value) => {
    if (Array.isArray(value)) {
      if (value.length > 0) {
        value.forEach((item) => {
          if (item) {
            queryParams.push(
              `${encodeURIComponent(key)}=${encodeURIComponent(item)}`
            );
          }
        });
      }
    } else if (value !== undefined && value !== null && value !== "") {
      queryParams.push(
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
      );
    }
  };
  for (const [key, value] of Object.entries(params)) {
    processValue(key, value);
  }
  return `${baseUrl}?${queryParams.join("&")}`;
}

export const getLogoutUrl = (origin) => {
  const env = getEnvironment(origin);
  const logoutUrl = logoutUrls[env] + origin;
  return logoutUrl;
};

const getEnvironment = (origin) => {
  if (origin.includes("dev.ril.com") || origin.includes("localhost")) {
    return "dev";
  }
  if (origin.includes("uat.ril.com")) {
    return "uat";
  }
  if (origin.includes("ril.com")) {
    return "prod";
  }
};

const REGEX = {
  characters: /^[a-zA-Z ]+$/,
};

export const validationCheck = (value, validation) => {
  return REGEX[validation].test(value);
};

export const summary_card_styles = {
  statParentStyle: {
    padding: "8px 12px",
    display: "flex",
    flexDirection: "column",
    borderRadius: "16px",
    height: "104px",
    minWidth: "172px",
    maxWidth: "172px",
    borderRadius: "10px",
  },
  statsStyle: [
    { fontSize: "18px", fontWeight: 900 },
    { color: "#000000A6", fontSize: "14px", fontWeight: 500 },
    { color: "#00B259", fontSize: "12px" },
    { color: "#000000A6" },
  ],
  cardHeaderStyle: {
    fontSize: "14px",
    color: "#000000A6",
    fontWeight: "500",
  },
};

const getDataSummaryCard = (data) => {
  if (typeof data === "string" && data.includes("%")) {
    return <CustomizedProgressBars value={parseFloat(data.split("%")[0])} />;
  } else {
    return data;
  }
};

export const getSummaryCardContent = (data) => {
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {data[0]?.map((ele, index) => {
        return (
          <div style={summary_card_styles.statsStyle[index]} key={index}>
            {getDataSummaryCard(ele)}
          </div>
        );
      })}
    </div>
  );
};

export const formatReportResponse = (response, payload) => {
  const { filtersApplied } = payload;
  const { filters } = response;
  filters.forEach((filter, filterIndex) => {
    const { parentFilterId } = filter;
    let filterValue;
    if (parentFilterId) {
      filterValue = filtersApplied.find((item) =>
        item.startsWith(parentFilterId + ":")
      );
      filterValue = filterValue ? filterValue.split(":").pop() : null;
      filters[filterIndex].values = filter.values.filter(
        (e) => e.parentId === parseInt(filterValue)
      );
    }
  });
  return [{ id: payload.dashboardId, reports: [response] }];
};

export function convertToCSV(data) {
  if (!data || data.length === 0) return "";

  const header = Object.keys(data[0]).join(",");
  const rows = data.map((item) => {
    return Object.values(item)
      .map((value) => `"${value}"`)
      .join(",");
  });

  return [header, ...rows].join("\n");
}

export function formatIndianNumber(value) {
  if (value === null || value === undefined || isNaN(value)) return "-";

  const num = Number(value);

  if (num >= 1e7) {
    return (num / 1e7).toFixed(2).replace(/\.00$/, "") + " Cr";
  } else if (num >= 1e5) {
    return (num / 1e5).toFixed(2).replace(/\.00$/, "") + " L";
  } else if (num >= 1e3) {
    return (num / 1e3).toFixed(2).replace(/\.00$/, "") + " K";
  } else {
    return num.toString();
  }
}
export const generateSQLQuery = (
  selectedTables,
  tableColumnsMap,
  joinRules,
  filterRules
) => {
  if (!selectedTables || selectedTables.length === 0) {
    return "-- No tables selected";
  }

  // SELECT clause
  const selectedColumnNames = [];

  for (const table of selectedTables) {
    const columns = tableColumnsMap[table];
    if (columns) {
      Object.entries(columns).forEach(([colName, isSelected]) => {
        if (isSelected) {
          selectedColumnNames.push(`${table}.${colName}`);
        }
      });
    }
  }

  const selectClause =
    selectedColumnNames.length > 0
      ? `SELECT ${selectedColumnNames.join(", ")}`
      : "SELECT *";

  // FROM clause (using the first selected table)
  const fromClause = `FROM ${selectedTables[0]}`;

  // JOIN clauses
  const joinClauses = joinRules
    .filter(
      (rule) =>
        rule.joinType &&
        rule.leftTable &&
        rule.rightTable &&
        rule.leftColumn &&
        rule.rightColumn
    )
    .map((rule) => {
      return `${rule.joinType.toUpperCase()} JOIN ${rule.rightTable} ON ${
        rule.leftTable
      }.${rule.leftColumn} = ${rule.rightTable}.${rule.rightColumn}`;
    });

  // WHERE clauses
  const whereClauses = filterRules
    .filter(
      (rule) => rule.table && rule.column && rule.operator && rule.value !== ""
    )
    .map((rule) => {
      const value =
        typeof rule.value === "string" && isNaN(rule.value)
          ? `'${rule.value}'`
          : rule.value;
      return `${rule.table}.${rule.column} ${rule.operator} ${value}`;
    });

  // Final SQL
  const sql = [
    selectClause,
    fromClause,
    ...joinClauses,
    whereClauses.length > 0 ? `WHERE ${whereClauses.join(" AND ")}` : "",
  ]
    .filter(Boolean)
    .join("\n");

  return sql + ";";
};

export const processPerformanceData = (
  apiData,
  filterMeta,
  appliedFilters,
  activeDuration
) => {
  const regionMap = {};
  const clusterMap = {};
  const storeToRegion = {};
  const storeToCluster = {};
  const storeLabelToId = {};
  const storeIdToLabel = {};

  const familyMap = {};
  const brandLabelToId = {};
  const brandIdToLabel = {};

  filterMeta.location_filter.regions.forEach((region) => {
    const regionName = region.name;
    regionMap[regionName] = regionName;

    region.clusters?.forEach((cluster) => {
      const clusterName = cluster.name;
      const storeIds = cluster.stores.map((store) => store.id);

      clusterMap[clusterName] = {
        regionName,
        storeIds,
      };

      cluster.stores.forEach((store) => {
        storeToRegion[store.id] = regionName;
        storeToCluster[store.id] = clusterName;
        storeLabelToId[store.name] = store.id;
        storeIdToLabel[store.id] = store.name;
      });
    });
  });

  filterMeta.product_filter.families.forEach((fam) => {
    familyMap[fam.id] = {
      name: fam.name,
      brands: fam.brands.reduce((acc, brand) => {
        acc[brand.id] = brand.name;
        brandLabelToId[brand.name] = brand.id;
        brandIdToLabel[brand.id] = brand.name;
        return acc;
      }, {}),
    };
  });

  const regionAgg = {};
  const familyAgg = {};

  localStorage.setItem("brandIdMap", JSON.stringify(brandIdToLabel));
  localStorage.setItem("clusterIdMap", JSON.stringify(clusterMap));
  localStorage.setItem("regionIdMap", JSON.stringify(regionMap));

  let totalSales = 0,
    totalSalesTarget = 0,
    totalMargin = 0,
    totalMarginTarget = 0,
    totalLFL = 0,
    totalDiscount = 0;

  const parseNum = (val) => parseFloat(val || 0);

  const matchByDuration = (timeSpan) => {
    // if (activeDuration === "Today") return timeSpan === "DAILY";
    // if (activeDuration === "WTD") return timeSpan === "WEEKLY";
    // if (activeDuration === "MTD") return timeSpan === "MONTHLY";
    return (activeDuration == timeSpan) 
  };

  apiData.forEach((entry) => {
    if (!matchByDuration(entry.time_span)) return;

    const { site_id, family_id, brand_id } = entry;

    const regionName = storeToRegion[site_id] || "Unknown Region";
    const clusterName = storeToCluster[site_id] || "Unknown Cluster";

    const sales = parseNum(entry.total_sales);
    const salesTarget = parseNum(entry.sales_target);
    const margin = parseNum(entry.total_margin);
    const marginTarget = parseNum(entry.margin_target);
    const lfl = parseNum(entry.lfl_sales);
    const discount = parseNum(entry.total_discount);

    totalSales += sales;
    totalSalesTarget += salesTarget;
    totalMargin += margin;
    totalMarginTarget += marginTarget;
    totalLFL += lfl;
    totalDiscount += discount;

    const familyName =
      familyMap[family_id]?.name || `Unknown Family (${family_id})`;
    const brandName =
      familyMap[family_id]?.brands?.[brand_id] || `Unknown Brand (${brand_id})`;

    if (!regionAgg[regionName]) {
      regionAgg[regionName] = {
        totalSales: 0,
        totalTarget: 0,
        totalMargin: 0,
        totalMarginTarget: 0,
        totalLFL: 0,
        totalDiscount: 0,
        stores: new Set(),
        regionId: regionName,
        clusterId: clusterName,
        storeId: storeIdToLabel[site_id] || site_id,
        familyName,
        brandName,
      };
    }

    const r = regionAgg[regionName];
    r.totalSales += sales;
    r.totalTarget += salesTarget;
    r.totalMargin += margin;
    r.totalMarginTarget += marginTarget;
    r.totalLFL += lfl;
    r.totalDiscount += discount;
    r.stores.add(site_id);

    if (!familyAgg[familyName]) {
      familyAgg[familyName] = {
        name: familyName,
        totalSales: 0,
        totalTarget: 0,
        totalMargin: 0,
        totalMarginTarget: 0,
        totalLFL: 0,
        totalDiscount: 0,
        children: [],
        familyId: family_id,
        regionId: regionName,
        clusterId: clusterName,
        storeId: site_id,
      };
    }

    const f = familyAgg[familyName];
    f.totalSales += sales;
    f.totalTarget += salesTarget;
    f.totalMargin += margin;
    f.totalMarginTarget += marginTarget;
    f.totalLFL += lfl;
    f.totalDiscount += discount;

    if (brandName) {
      let brand = f.children.find((c) => c.name === brandName);
      if (!brand) {
        brand = {
          name: brandName,
          totalSales: 0,
          totalTarget: 0,
          totalMargin: 0,
          totalMarginTarget: 0,
          totalLFL: 0,
          totalDiscount: 0,
        };
        f.children.push(brand);
      }

      brand.totalSales += sales;
      brand.totalTarget += salesTarget;
      brand.totalMargin += margin;
      brand.totalMarginTarget += marginTarget;
      brand.totalLFL += lfl;
      brand.totalDiscount += discount;
    }
  });

  const regionData = Object.entries(regionAgg).map(([region, r]) => {
    return {
      region,
      pmi: safeRatio(r.totalMargin, 1),
      pmiPercent: safePercent(r.totalMargin, r.totalMarginTarget) + "%",
      sales: safeRatio(r.totalSales, 1),
      salesPercent: safePercent(r.totalSales, r.totalTarget) + "%",
      lfl: safePercent(r.totalLFL, r.totalTarget) + "%",
      discount: safePercent(r.totalDiscount, r.totalSales) + "%",
      stores: r.storeId,
      region: r.regionId,
      cluster: r.clusterId,
      family: r.familyName,
      brands: r.brandName,
      storesCount: r.stores.size,
    };
  });

  const productFamilyData = Object.values(familyAgg).map((f) => {
    return {
      name: f.name,
      pmi: safeRatio(f.totalMargin, 1),
      pmiPercent: safePercent(f.totalMargin, f.totalMarginTarget, 1) + "%",
      sales: safeRatio(f.totalSales, 1),
      salesPercent: f.totalTarget
        ? Math.round((f.totalSales / f.totalTarget) * 100)
        : 0,
      lfl: +safePercent(f.totalLFL, f.totalTarget, 1),
      discount: +safePercent(f.totalDiscount, f.totalSales, 1),
      family: f.familyId,
      region: f.regionId,
      cluster: f.clusterId,
      stores: f.storeId,
      children: f.children.map((b) => ({
        name: b.name,
        pmi: safeRatio(b.totalMargin, 1),
        pmiPercent: safePercent(b.totalMargin, b.totalMarginTarget) + "%",
        sales: safeRatio(b.totalSales, 1),
        salesPercent: b.totalTarget
          ? Math.round((b.totalSales / b.totalTarget) * 100)
          : 0,
        lfl: +safePercent(b.totalLFL, b.totalTarget, 1),
        discount: +safePercent(b.totalDiscount, b.totalSales, 1),
      })),
    };
  });

  const sortedProductFamilyData = productFamilyData.sort((a, b) => {
    const salesA = parseFloat(a.sales.replace(/[₹L]/g, ""));
    const salesB = parseFloat(b.sales.replace(/[₹L]/g, ""));
    return salesB - salesA;
  });

  const metricsData = [
    {
      title: "PMI (Profit Margin Index)",
      actual: +(totalMargin / 100000).toFixed(1),
      target: +(totalMarginTarget / 100000).toFixed(2),
      percent: +safePercent(totalMargin, totalMarginTarget, 1),
      displayActual: safeRatio(totalMargin, 1),
      displayTarget: safeRatio(totalMarginTarget, 1),
    },
    {
      title: "Sales",
      actual: +(totalSales / 100000).toFixed(1),
      target: +(totalSalesTarget / 100000).toFixed(2),
      percent: +safePercent(totalSales, totalSalesTarget, 1),
      displayActual: "₹" + (totalSales / 100000).toFixed(1) + "L",
      displayTarget: "₹" + (totalSalesTarget / 100000).toFixed(2) + "L",
    },
    {
      title: "LFL sales",
      actual: +(totalLFL / 100000).toFixed(1),
      target: +(totalSalesTarget / 100000).toFixed(2),
      percent: +safePercent(totalLFL, totalSalesTarget, 1),
      displayActual: "₹" + (totalLFL / 100000).toFixed(1) + "L",
      displayTarget: "₹" + (totalSalesTarget / 100000).toFixed(2) + "L",
    },
    {
      title: "Discounting",
      actual: +safePercent(totalDiscount, totalSales, 1),
      target: 8.6,
      percent: totalSales
        ? +((((totalDiscount / totalSales) * 100) / 8.6) * 100).toFixed(2)
        : "0.00",
    },
  ];

  return {
    regionData,
    productFamilyData: sortedProductFamilyData,
    metricsData,
  };
};

export function removeEmptyKeys(obj) {
  const result = { ...obj }; // Shallow copy

  for (let key in result) {
    if (result[key].length === 0) {
      delete result[key];
    }
  }
  return result;
}


export function clean_home_session_data(){
    sessionStorage.removeItem("taskName");
    sessionStorage.removeItem("region");
    sessionStorage.removeItem("taskType");
    sessionStorage.removeItem("storeCode");
    sessionStorage.removeItem("articleTaskId");
    sessionStorage.removeItem("articleStoreCode");
    sessionStorage.removeItem("home_timeFrame");
    sessionStorage.removeItem("home_dateRange");
    sessionStorage.removeItem("home_searchQuery");
    sessionStorage.removeItem("home_tabValue");
    sessionStorage.removeItem("home_selectedValues");
    sessionStorage.removeItem("home_filtersApplied");
    sessionStorage.removeItem("home_hasFiltersChanged");
    sessionStorage.removeItem("home_lastAppliedValues");
    sessionStorage.removeItem("home_lastAppliedTimeFrame");
    sessionStorage.removeItem("home_lastAppliedDateRange");
    sessionStorage.removeItem("home_lastAppliedSearchQuery");
    sessionStorage.removeItem("apiFilters");
    sessionStorage.removeItem("detail_timeFrame");
    sessionStorage.removeItem("detail_dateRange");
    sessionStorage.removeItem("detail_searchQuery");
    sessionStorage.removeItem("detail_tabValue");
    sessionStorage.removeItem("detail_selectedValues");
    sessionStorage.removeItem("detail_filtersApplied");
    sessionStorage.removeItem("detail_hasFiltersChanged");
    sessionStorage.removeItem("detail_lastAppliedValues");
    sessionStorage.removeItem("detail_lastAppliedTimeFrame");
    sessionStorage.removeItem("detail_lastAppliedDateRange");
    sessionStorage.removeItem("detail_lastAppliedSearchQuery");
    sessionStorage.removeItem("cluster");
    sessionStorage.removeItem("detail_apiFilters");
    sessionStorage.removeItem("home_apiFilters");
    sessionStorage.removeItem("articleTaskStatus");
    sessionStorage.removeItem("statusList");
    sessionStorage.removeItem("criticalCount");
    sessionStorage.removeItem("isEventClickFire");
    sessionStorage.removeItem("OrderType");
    
}


export const getDateRange = (rangeType) => {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  let startDate;
  let endDate;

  switch(rangeType) {
    case "WTD": // Week-to-Date, start Monday
      const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, ...
      
      // If today is Monday, return last week's dates
      if (dayOfWeek === 1) {
        // Last week's Monday
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 7);
        const diffToLastMonday = (startDate.getDay() + 6) % 7;
        startDate.setDate(startDate.getDate() - diffToLastMonday);
        
        // Last week's Sunday (6 days after Monday)
        endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + 6);
      } else {
        // Current week's Monday
        startDate = new Date(today);
        const diffToMonday = (dayOfWeek + 6) % 7;
        startDate.setDate(today.getDate() - diffToMonday);
        endDate = today; // Use today for end date, not yesterday
      }
      break;

    case "MTD": 
      // If today is the 1st of the month, return last month's dates
      if (today.getDate() === 1) {
        // Last month's first day
        startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        // Last month's last day
        endDate = new Date(today.getFullYear(), today.getMonth(), 0);
      } else {
        // Current month's first day
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = today; // Use today for end date, not yesterday
      }
      break;

    case "YSTD":
      startDate = new Date(yesterday);
      endDate = yesterday; // Use yesterday for end date
      break;
    case "FTD": // Today (default)
    default:
      startDate = new Date(today); // Today's date
      endDate = today; // Use today for end date
      break;
  }
 
  // Format dates to match API requirements - manually format to avoid timezone issues
  const formatStartDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}T00:00:00`;
  };

  const formatEndDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}T23:59:59`;
  };

  return {
    startDate: formatStartDate(startDate),
    endDate: formatEndDate(endDate),
  };
};

/** SEL period stats must not query data before this day (API availability). */
export const SEL_STATS_MIN_START_DATE = "2026-04-19T00:00:00";

export const clampSelStatsDate = (dateString, isEndDate = false) => {
  if (!dateString || dateString.length < 10) return dateString;
  const minDay = SEL_STATS_MIN_START_DATE.slice(0, 10);
  const dateDay = dateString.slice(0, 10);
  
  if (dateDay < minDay) {
    if (isEndDate) {
      // For end date, return the end of min day
      return `${minDay}T23:59:59`;
    }
    return `${minDay}T00:00:00`;
  }
  return dateString;
};

// Keep the old function for backward compatibility
export const clampSelStatsStartDate = (startDate) => {
  return clampSelStatsDate(startDate, false);
};

// Add a new function for end date
export const clampSelStatsEndDate = (endDate) => {
  return clampSelStatsDate(endDate, true);
};

/**
 * SEL-specific date range. Overrides WTD on Monday and MTD on the 1st so that
 * SEL reports show today-only instead of the previous week/month window.
 */
export const getSelDateRange = (rangeType) => {
  const today = new Date();
  const isMondayWTD = rangeType === "WTD" && today.getDay() === 1;
  const isFirstOfMonthMTD = rangeType === "MTD" && today.getDate() === 1;

  if (isMondayWTD || isFirstOfMonthMTD) {
    const y = today.getFullYear();
    const m = String(today.getMonth() + 1).padStart(2, "0");
    const d = String(today.getDate()).padStart(2, "0");
    let startDate = `${y}-${m}-${d}T00:00:00`;
    let endDate = `${y}-${m}-${d}T23:59:59`;
    
    // Clamp both dates
    startDate = clampSelStatsStartDate(startDate);
    endDate = clampSelStatsEndDate(endDate);
    
    return {
      startDate,
      endDate,
    };
  }

  const range = getDateRange(rangeType);
  // Clamp both dates for all range types
  range.startDate = clampSelStatsStartDate(range.startDate);
  range.endDate = clampSelStatsEndDate(range.endDate);
  
  return range;
};


export function toPermissionMap(permissionsArray=[], { normalize = false } = {}) {
  const map = {};

  permissionsArray?.forEach(feature => {
    const featureName = feature.featureName;
    if (!featureName) return;

    if (!map[featureName]) map[featureName] = [];

    (feature.permissions || []).forEach(p => {
      if (!p || typeof p.name !== 'string') return;
      let pname = p.name.trim();

      if (normalize) {
        pname = pname.toLowerCase();
        // small normalization fixes (add more if you want)
        if (pname === 'darft') pname = 'draft';
      }

      if (!map[featureName].includes(pname)) {
        map[featureName].push(pname);
      }
    });
  });

  return map;
}

// utils/permissionUtils.js
// export const hasPermission = (profileData, feature, requiredPermission) => {
//   if (!profileData) return false;

//   const { featuresPermission = [], permissionMap = {} } = profileData;

//   return (
//     featuresPermission?.includes(feature) &&
//     permissionMap[feature]?.map(p => p.toLowerCase())?.includes(requiredPermission.toLowerCase())
//   );
// };

// export const labelConvert=(text)=>text?.split(' ')?.map(item=>item?.toLowerCase())?.join('-')


// helper/utils.js

export const checkIsAdmin=(profileData) => {
  const adminList=['Head','HO','BOC','IT']
  if (!profileData) return false;

  const { jobRoleName } = profileData;
  return adminList.includes(jobRoleName);
}

export const hasPermission = (profileData, feature, requiredPermission) => {
  if (!profileData) return false;

  // First, check if using new nested structure (permissions.modules exists)
  if (profileData.permissions && profileData.permissions.modules) {
    return checkNestedPermission(profileData.permissions.modules, feature, requiredPermission);
  }

  // Fall back to old structure for backward compatibility
  const { featuresPermission = [], permissionMap = {} } = profileData;
  return (
    featuresPermission?.includes(feature) &&
    permissionMap[feature]?.map(p => p.toLowerCase())?.includes(requiredPermission.toLowerCase())
  );
};

const findFeaturePermissions = (node, feature) => {
  if (!node || typeof node !== "object") return null;

  if (node.featureName === feature && node.permissions) {
    return node.permissions;
  }

  const children = node.subFeatures || node.features;
  if (!children) return null;

  for (const childId in children) {
    const found = findFeaturePermissions(children[childId], feature);
    if (found) return found;
  }

  return null;
};

// Helper function to check permissions in new nested structure
const checkNestedPermission = (modules, feature, requiredPermission) => {
  if (!modules) return false;

  requiredPermission = requiredPermission.toLowerCase();

  // Iterate through all modules
  for (const moduleId in modules) {
    const module = modules[moduleId];

    if (!module) continue;

    // check for module permission
    if (module.moduleName === feature) {
      // should also check if module has permissions array
      return true;
    }

    const permissions = findFeaturePermissions({ features: module.features }, feature);
    if (permissions) {
      return permissions.some(
        (permission) => permission?.name?.toLowerCase() === requiredPermission
      );
    }
  }

  return false;
};

export const labelConvert = (text) => text?.split(' ')?.map(item => item?.toLowerCase())?.join('-');
export const toTitleCase = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());